/**
 * WebSocket client with auto-reconnect.
 * Replaces Supabase Realtime subscriptions.
 */

type MessageHandler = (message: { type: string; data: unknown }) => void

const WS_URL = import.meta.env.VITE_WS_URL || ''

class RealtimeClient {
  private ws: WebSocket | null = null
  private handlers = new Map<string, Set<MessageHandler>>()
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private reconnectAttempts = 0
  private maxReconnectAttempts = 10
  private token: string | null = null

  connect(token: string) {
    this.token = token
    this.doConnect()
  }

  private doConnect() {
    if (!WS_URL || !this.token) return

    try {
      this.ws = new WebSocket(`${WS_URL}?token=${this.token}`)

      this.ws.onopen = () => {
        console.log('[WS] Connected')
        this.reconnectAttempts = 0
      }

      this.ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data)
          const handlers = this.handlers.get(message.type)
          if (handlers) {
            for (const handler of handlers) {
              handler(message)
            }
          }
          // Also notify catch-all handlers
          const allHandlers = this.handlers.get('*')
          if (allHandlers) {
            for (const handler of allHandlers) {
              handler(message)
            }
          }
        } catch (e) {
          console.error('[WS] Failed to parse message:', e)
        }
      }

      this.ws.onclose = () => {
        console.log('[WS] Disconnected')
        this.scheduleReconnect()
      }

      this.ws.onerror = (error) => {
        console.error('[WS] Error:', error)
      }
    } catch (error) {
      console.error('[WS] Connect failed:', error)
      this.scheduleReconnect()
    }
  }

  private scheduleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.warn('[WS] Max reconnect attempts reached')
      return
    }

    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000)
    this.reconnectAttempts++

    this.reconnectTimer = setTimeout(() => {
      console.log(`[WS] Reconnecting (attempt ${this.reconnectAttempts})...`)
      this.doConnect()
    }, delay)
  }

  on(type: string, handler: MessageHandler): () => void {
    if (!this.handlers.has(type)) {
      this.handlers.set(type, new Set())
    }
    this.handlers.get(type)!.add(handler)

    return () => {
      this.handlers.get(type)?.delete(handler)
    }
  }

  disconnect() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    if (this.ws) {
      this.ws.close()
      this.ws = null
    }
    this.handlers.clear()
    this.token = null
  }
}

export const realtimeClient = new RealtimeClient()
